/**
 * Cart discount code: apply via Cart AJAX API, persist in sessionStorage,
 * and carry into checkout (including ATC / product CTAs).
 *
 * Source of truth is cart.js money state — never show an "applied" row unless
 * the cart actually has a positive total_discount / allocated amount.
 */
(function () {
  var STORAGE_KEY = 'cs_cart_discount_code';

  function rootUrl() {
    var root =
      (window.Shopify && Shopify.routes && Shopify.routes.root) ||
      (window.Theme && Theme.routes && Theme.routes.root_url) ||
      '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function getStoredCode() {
    try {
      return (sessionStorage.getItem(STORAGE_KEY) || '').trim();
    } catch (e) {
      return '';
    }
  }

  function setStoredCode(code) {
    try {
      if (code) {
        sessionStorage.setItem(STORAGE_KEY, String(code).trim());
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function appendDiscountToUrl(url, code) {
    if (!code || !url) return url;
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.pathname.indexOf('/checkout') === -1) return url;
      if (!parsed.searchParams.get('discount')) {
        parsed.searchParams.set('discount', code);
      }
      return parsed.pathname + parsed.search + parsed.hash;
    } catch (e) {
      if (url.indexOf('discount=') !== -1) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'discount=' + encodeURIComponent(code);
    }
  }

  function checkoutUrlWithDiscount(code) {
    return appendDiscountToUrl(rootUrl() + 'checkout', (code || getStoredCode() || '').trim());
  }

  function getRenderableDiscountApps(cart) {
    return (cart && cart.cart_level_discount_applications
      ? cart.cart_level_discount_applications
      : []
    ).filter(function (app) {
      if (!app) return false;
      if (app.type && app.type !== 'discount_code') return false;
      return Number(app.total_allocated_amount || 0) > 0;
    });
  }

  function discountMoneyApplied(cart) {
    return Number((cart && cart.total_discount) || 0) > 0;
  }

  /** True only when the code is both present and actually reducing cart money. */
  function cartHasDiscount(cart, code) {
    if (!cart || !code) return false;
    if (!discountMoneyApplied(cart) && !getRenderableDiscountApps(cart).length) {
      return false;
    }

    var needle = String(code).toUpperCase();

    if (Array.isArray(cart.discount_codes)) {
      for (var i = 0; i < cart.discount_codes.length; i++) {
        var entry = cart.discount_codes[i];
        if (
          entry &&
          String(entry.code || '').toUpperCase() === needle &&
          entry.applicable !== false
        ) {
          return discountMoneyApplied(cart) || getRenderableDiscountApps(cart).length > 0;
        }
      }
    }

    var apps = getRenderableDiscountApps(cart);
    for (var j = 0; j < apps.length; j++) {
      if (String(apps[j].title || '').toUpperCase() === needle) return true;
    }

    if (Array.isArray(cart.items)) {
      for (var k = 0; k < cart.items.length; k++) {
        var discounts = cart.items[k].discounts || [];
        for (var m = 0; m < discounts.length; m++) {
          if (
            String(discounts[m].title || '').toUpperCase() === needle &&
            Number(discounts[m].amount || 0) > 0
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  function fetchCart() {
    return fetch(rootUrl() + 'cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('cart.js failed');
      return res.json();
    });
  }

  function discountExplicitlyRejected(cart, code) {
    if (!cart || !code || !Array.isArray(cart.discount_codes)) return false;
    var needle = String(code).toUpperCase();
    for (var i = 0; i < cart.discount_codes.length; i++) {
      var entry = cart.discount_codes[i];
      if (entry && String(entry.code || '').toUpperCase() === needle && entry.applicable === false) {
        return true;
      }
    }
    return false;
  }

  function postDiscount(code) {
    return fetch(rootUrl() + 'cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ discount: code }),
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/update.js failed');
      return res.json();
    });
  }

  function applyDiscount(code) {
    var normalized = (code || '').trim();
    return postDiscount(normalized)
      .then(function () {
        return fetchCart();
      })
      .then(function (cart) {
        if (!normalized) {
          setStoredCode('');
          return cart;
        }

        if (discountExplicitlyRejected(cart, normalized)) {
          var rejected = new Error('invalid_discount');
          rejected.cart = cart;
          throw rejected;
        }

        if (!cartHasDiscount(cart, normalized)) {
          // Not reflected in cart money — keep code only for checkout?discount=
          var soft = new Error('checkout_fallback');
          soft.cart = cart;
          soft.soft = true;
          setStoredCode(normalized);
          throw soft;
        }

        setStoredCode(normalized);
        return cart;
      });
  }

  function goToCheckout(code) {
    window.location.href = checkoutUrlWithDiscount(code);
  }

  function bindGlobalCheckoutCarriers() {
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!(form instanceof HTMLFormElement)) return;

        // Cart page has its own apply-then-checkout flow.
        if (form.querySelector('[data-cart-discount-code]')) return;

        var submitter = event.submitter;
        if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

        var code = getStoredCode();
        if (!code) return;

        event.preventDefault();
        goToCheckout(code);
      },
      true
    );

    document.addEventListener(
      'click',
      function (event) {
        var link =
          event.target && event.target.closest
            ? event.target.closest('a[href*="/checkout"]')
            : null;
        if (!link) return;
        var code = getStoredCode();
        if (!code) return;
        var next = appendDiscountToUrl(link.getAttribute('href'), code);
        if (next && next !== link.getAttribute('href')) {
          link.setAttribute('href', next);
        }
      },
      true
    );
  }

  function initCartDiscountField() {
    var root = document.querySelector('[data-cart-discount-code]');
    if (!root) return;

    var input = root.querySelector('[data-cart-discount-input]');
    var applyBtn = root.querySelector('[data-cart-discount-apply]');
    var messageEl = root.querySelector('[data-cart-discount-message]');
    var moneyFormat = root.getAttribute('data-money-format') || '';
    var stringsEl = root.querySelector('[data-cart-discount-strings]');
    var strings = {};

    if (stringsEl) {
      try {
        strings = JSON.parse(stringsEl.textContent || '{}');
      } catch (e) {
        strings = {};
      }
    }

    function t(key, fallback) {
      return strings[key] || fallback || key;
    }

    function setMessage(text, type) {
      if (!messageEl) return;
      if (!text) {
        messageEl.hidden = true;
        messageEl.textContent = '';
        messageEl.classList.remove('is-success', 'is-error');
        return;
      }
      messageEl.hidden = false;
      messageEl.textContent = text;
      messageEl.classList.toggle('is-success', type === 'success');
      messageEl.classList.toggle('is-error', type === 'error');
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat);
      }
      return (Number(cents) / 100).toFixed(2);
    }

    function getDiscountIconHtml() {
      var iconTpl = root.querySelector('[data-cart-discount-icon]');
      return iconTpl ? iconTpl.innerHTML.trim() : '';
    }

    function getCloseIconHtml() {
      var iconTpl = root.querySelector('[data-cart-discount-close-icon]');
      return iconTpl ? iconTpl.innerHTML.trim() : '×';
    }

    function renderDiscountList(cart) {
      var list = document.querySelector('[data-cart-discounts]');
      if (!list) return;

      var apps = getRenderableDiscountApps(cart);
      // Only show the applied row when money is actually discounted.
      if (!apps.length || !discountMoneyApplied(cart)) {
        list.innerHTML = '';
        root.classList.remove('has-applied-discount');
        return;
      }

      var iconHtml = getDiscountIconHtml();
      var closeHtml = getCloseIconHtml();
      var removeLabel = t('remove', 'Remove discount code');
      var html = '';
      for (var i = 0; i < apps.length; i++) {
        var app = apps[i];
        html +=
          '<li class="discount-list-item cart-discount">' +
          '<span class="cart-discount-title">' +
          iconHtml +
          (app.title || '') +
          '</span>' +
          '<span class="cart-discount-price">-<span class="money">' +
          formatMoney(app.total_allocated_amount || 0) +
          '</span></span>' +
          '<button type="button" class="cart-discount-remove" data-cart-discount-remove aria-label="' +
          removeLabel.replace(/"/g, '&quot;') +
          '">' +
          closeHtml +
          '</button>' +
          '</li>';
      }
      list.innerHTML = html;
      root.classList.add('has-applied-discount');
    }

    function updateTotals(cart) {
      document.querySelectorAll('[data-cart-total]').forEach(function (el) {
        var formatted = formatMoney(cart.total_price);
        // Preserve YMQ wrapper used on FR carts when present.
        var ymq = el.querySelector('.ymq_cart_total_price');
        if (ymq) {
          ymq.innerHTML = formatted;
        } else {
          el.innerHTML = formatted;
        }
      });
    }

    function syncFromCart(cart) {
      renderDiscountList(cart);
      updateTotals(cart);

      var apps = getRenderableDiscountApps(cart);
      var effective = apps.length > 0 && discountMoneyApplied(cart);

      if (effective) {
        var codeFromCart = '';
        if (Array.isArray(cart.discount_codes)) {
          for (var i = 0; i < cart.discount_codes.length; i++) {
            var entry = cart.discount_codes[i];
            if (entry && entry.applicable !== false && entry.code) {
              codeFromCart = String(entry.code);
              break;
            }
          }
        }
        if (!codeFromCart && apps[0] && apps[0].title) {
          codeFromCart = String(apps[0].title);
        }
        if (codeFromCart) {
          setStoredCode(codeFromCart);
          if (input) input.value = codeFromCart;
        }
        setMessage('');
      } else {
        // Soft-stored code may remain for checkout?discount=, but do not
        // pretill as "applied" UI. Leave input empty unless user typed.
        root.classList.remove('has-applied-discount');
      }
    }

    /**
     * Clear stuck / non-effecting discount codes so a stale Liquid SSR row
     * cannot stick around when cart money was never discounted.
     */
    function reconcileCartDiscounts(cart) {
      var apps = getRenderableDiscountApps(cart);
      var codes = cart.discount_codes || [];
      var hasCodes = codes.length > 0;
      var hasRejected = codes.some(function (c) {
        return c && c.applicable === false;
      });
      var moneyOk = discountMoneyApplied(cart);
      var effective = apps.length > 0 && moneyOk;

      // Ghost: code attached or apps reported, but cart money not reduced.
      var phantom =
        (hasCodes || (cart.cart_level_discount_applications || []).length > 0) && !effective;

      if (phantom || hasRejected) {
        return postDiscount('')
          .then(function () {
            return fetchCart();
          })
          .then(function (fresh) {
            // Keep soft checkout code only if it was never a rejected ghost.
            if (hasRejected || phantom) {
              // If it was never really applied, drop storage so cart looks clean.
              setStoredCode('');
              if (input) input.value = '';
            }
            syncFromCart(fresh);
            return fresh;
          })
          .catch(function () {
            // Even if clear fails, wipe the misleading UI.
            setStoredCode('');
            if (input) input.value = '';
            var list = document.querySelector('[data-cart-discounts]');
            if (list) list.innerHTML = '';
            root.classList.remove('has-applied-discount');
            syncFromCart(cart);
            return cart;
          });
      }

      syncFromCart(cart);
      return Promise.resolve(cart);
    }

    function getInputCode() {
      return (input && input.value ? input.value : '').trim();
    }

    function setBusy(busy) {
      if (applyBtn) applyBtn.disabled = !!busy;
      if (input) input.disabled = !!busy;
      root.classList.toggle('is-busy', !!busy);
    }

    function clearCode() {
      setBusy(true);
      setMessage('');

      return applyDiscount('')
        .then(function (cart) {
          if (input) input.value = '';
          setStoredCode('');
          syncFromCart(cart);
          return true;
        })
        .catch(function () {
          if (input) input.value = '';
          setStoredCode('');
          return fetchCart()
            .then(function (cart) {
              syncFromCart(cart);
            })
            .catch(function () {
              var list = document.querySelector('[data-cart-discounts]');
              if (list) list.innerHTML = '';
            });
        })
        .finally(function () {
          setBusy(false);
        });
    }

    function applyCode() {
      var code = getInputCode();
      if (!code) {
        setMessage(t('empty', 'Please enter a discount code'), 'error');
        return Promise.resolve(false);
      }

      setBusy(true);
      setMessage(t('applying', 'Applying…'), '');

      return applyDiscount(code)
        .then(function (cart) {
          syncFromCart(cart);
          setMessage('');
          return true;
        })
        .catch(function (err) {
          if (err && err.message === 'invalid_discount') {
            setStoredCode('');
            setMessage(t('invalid', 'Enter a valid discount code'), 'error');
            if (err.cart) syncFromCart(err.cart);
            return false;
          }

          // Soft fallback: keep for checkout URL, never show as applied row.
          setStoredCode(code);
          if (err && err.cart) syncFromCart(err.cart);
          setMessage(
            t('checkout_fallback', 'Discount will be applied at checkout'),
            'success'
          );
          return true;
        })
        .finally(function () {
          setBusy(false);
        });
    }

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        applyCode();
      });
    }

    document.addEventListener('click', function (event) {
      var removeBtn =
        event.target && event.target.closest
          ? event.target.closest('[data-cart-discount-remove]')
          : null;
      if (!removeBtn) return;
      event.preventDefault();
      clearCode();
    });

    if (input) {
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Enter') {
          event.preventDefault();
          applyCode();
        }
      });
    }

    var cartForm = root.closest('form');
    if (cartForm) {
      cartForm.addEventListener(
        'submit',
        function (event) {
          var submitter = event.submitter;
          if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

          var code = getInputCode() || getStoredCode();
          if (!code) return;

          event.preventDefault();
          event.stopPropagation();
          setStoredCode(code);

          applyCode().finally(function () {
            goToCheckout(code);
          });
        },
        true
      );

      var walletHost = cartForm.querySelector('.additional-checkout-buttons');
      if (walletHost) {
        walletHost.addEventListener(
          'pointerdown',
          function () {
            var code = getInputCode() || getStoredCode();
            if (!code) return;
            setStoredCode(code);
            applyDiscount(code).catch(function () {
              /* ignore */
            });
          },
          true
        );
      }
    }

    // Immediately hide any Liquid SSR discount row until cart.js confirms it.
    var listEl = document.querySelector('[data-cart-discounts]');
    if (listEl) listEl.innerHTML = '';

    fetchCart()
      .then(reconcileCartDiscounts)
      .catch(function () {
        if (listEl) listEl.innerHTML = '';
      });

    // After quantity AJAX, Liquid may inject a stale discount row — re-check.
    window.addEventListener('cartcount:update', function () {
      fetchCart()
        .then(reconcileCartDiscounts)
        .catch(function () {
          /* ignore */
        });
    });
  }

  window.CsCartDiscount = {
    STORAGE_KEY: STORAGE_KEY,
    rootUrl: rootUrl,
    getStoredCode: getStoredCode,
    setStoredCode: setStoredCode,
    appendDiscountToUrl: appendDiscountToUrl,
    checkoutUrlWithDiscount: checkoutUrlWithDiscount,
    cartHasDiscount: cartHasDiscount,
    fetchCart: fetchCart,
    applyDiscount: applyDiscount,
    goToCheckout: goToCheckout,
  };

  function boot() {
    bindGlobalCheckoutCarriers();
    initCartDiscountField();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
